import React, { useId } from "react";
import { bandColor } from "../lib/perf";
import { useTween } from "../lib/useTween";

const rgb = { danger: "229,57,53", warning: "251,140,0", good: "67,160,71", excellent: "0,230,118" };

// HITMAX signature capsule: dual-layer luminous ring.
// Outer = segmented "bars" that light up progressively; inner = smooth glowing progress arc.
export const CapsuleRing = ({ pct, label, band = "good", size = 96, segments = 30 }) => {
  const color = bandColor(band);
  const base = rgb[band] || "212,175,55";
  const v = useTween(pct);
  const filled = (v / 100) * segments;

  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 5;
  const rInner = size / 2 - 13;
  const seg = 360 / segments;
  const gap = 3.2;

  const polar = (angle, rr) => {
    const a = ((angle - 90) * Math.PI) / 180;
    return [cx + rr * Math.cos(a), cy + rr * Math.sin(a)];
  };
  const arc = (start, end, rr) => {
    const [x1, y1] = polar(start, rr);
    const [x2, y2] = polar(end, rr);
    const large = end - start <= 180 ? 0 : 1;
    return `M ${x1} ${y1} A ${rr} ${rr} 0 ${large} 1 ${x2} ${y2}`;
  };

  const innerCirc = 2 * Math.PI * rInner;
  const innerOffset = innerCirc * (1 - v / 100);
  const gid = "c" + useId().replace(/:/g, "");

  return (
    <div className="flex flex-col items-center gap-1.5 shrink-0" data-testid={`capsule-${label}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} style={{ filter: `drop-shadow(0 0 6px rgba(${base},0.45))` }}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={`rgba(${base},1)`} />
              <stop offset="100%" stopColor={`rgba(${base},0.55)`} />
            </linearGradient>
          </defs>

          {/* outer segmented bars */}
          {Array.from({ length: segments }).map((_, i) => {
            const start = i * seg + gap / 2;
            const end = (i + 1) * seg - gap / 2;
            const on = i < Math.round(filled);
            return (
              <path
                key={i}
                d={arc(start, end, rOuter)}
                fill="none"
                stroke={on ? `url(#${gid})` : "rgba(255,255,255,0.07)"}
                strokeWidth={on ? 5 : 3.5}
                strokeLinecap="round"
                style={{ transition: "stroke 0.35s ease, stroke-width 0.35s ease", transitionDelay: `${i * 20}ms` }}
              />
            );
          })}

          {/* inner smooth progress ring */}
          <circle cx={cx} cy={cy} r={rInner} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="2.5" />
          <circle
            cx={cx}
            cy={cy}
            r={rInner}
            fill="none"
            stroke={color}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={innerCirc}
            strokeDashoffset={innerOffset}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ opacity: 0.55, filter: `drop-shadow(0 0 3px ${color})` }}
          />
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-numeral font-black leading-none tabular-nums" style={{ color, fontSize: size * 0.28 }}>
            {Math.round(v)}
          </span>
          <span className="text-[9px] font-bold text-white/40 -mt-0.5">%</span>
        </div>
      </div>
      <span className="text-xs font-bold uppercase tracking-widest text-white/60">{label}</span>
    </div>
  );
};
